import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy, Optional, Input, Output, EventEmitter, HostBinding, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgbDateStruct, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { sanitizeInputForCrossSiteScriptCharactersDescription, stripHtmlToText, nonWhitespaceHtmlValidator, getStatusPageErrorMessage } from '../../../../../_theme/core/_base/curd/utils/validators-util';
import { Maintenance } from '../../../shared/models/dashboard.models';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { AuthService } from 'src/app/_theme/core/users/Auth/_services/auth.service';
import { LayoutUtilsService, MessageType } from '../../../../../_theme/core/_base/curd';
import { environment } from 'src/environments/environment';
import { CompleteMaintenancePayload, UpdateMaintenancePayload } from '../../models/status-page-api.models';
import { AuthHTTPService } from 'src/app/_theme/core/users/Auth/_services/auth-http';
import { UserstorageService } from 'src/app/_theme/core/users/userstorage/userstorage.service';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { CustomSelectComponent } from '../../components/custom-select/custom-select.component';
import { DateTimePickerComponent } from '../../components/date-time-picker/date-time-picker.component';
import { QuillModule } from 'ngx-quill';
import {
  getMaintenanceStatusLabel as getMaintenanceStatusDisplayLabel,
  normalizeMaintenanceStatusClass
} from '../../models/status-pill.utils';
import { StatusDrawerQuillKeyboardDirective } from '../../directives/status-drawer-quill-keyboard.directive';
import { normalizeStatusPageRichText } from '../../models/status-page-rich-text-normalizer.utils';
import { StatusPageRichTextPipe } from '../../pipes/status-page-rich-text.pipe';
import {
  formatDateInTimeZone,
  getTimeZoneAbbreviation
} from '../../models/status-time.utils';
import { StatusPageProfileTimeZoneService } from '../../../services/status-page-profile-time-zone.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-status-page-manage-maintenance-dialog',
  templateUrl: './manage-maintenance-dialog.component.html',
  styleUrls: [
    './manage-maintenance-dialog.component.scss',
    './manage-maintenance-dialog.component.dark.scss',
    './manage-maintenance-dialog.component.layout.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    NgbModule,
    QuillModule,
    CustomSelectComponent,
    DateTimePickerComponent,
    StatusDrawerQuillKeyboardDirective,
    StatusPageRichTextPipe
  ]
})
export class StatusPageManageMaintenanceDialogComponent implements OnInit, OnDestroy {

  readonly messageEditorModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link']
    ]
  };

  @Input() dataInput?: Maintenance;
  @Input() layout: 'dialog' | 'drawer' = 'dialog';
  @Input() allowDelete: boolean = true;
  @Output() closed = new EventEmitter<any>();
  @Output() refresh = new EventEmitter<void>();
  @Output() toggleExpand = new EventEmitter<void>();
  @Input() isExpanded: boolean = false;
  @Input() showExpandToggle: boolean = true;
  @HostBinding('class.drawer-mode') get isDrawerMode() {
    return this.layout === 'drawer';
  }

  get maintenanceData(): Maintenance | null {
    return this.dataInput ?? this.data ?? null;
  }

  // Forms
  updateForm!: FormGroup;

  showDateEditing = false;
  descriptionLength: number | null = null;
  isSaving = false;
  accountTZ: string | null = null;
  profileTimeZone: string | null = null;
  locale: string = 'en-US';
  minDateTime: Date | null = null;
  minDate: NgbDateStruct | null = null;
  isTimeZoneLoading = false;
  accountTzName: string | null = null;
  private accountTimeZoneResolved = false;
  private profileTimeZoneResolved = false;
  private timeZoneSourcesInitialized = false;
  private revalidationIntervalId: ReturnType<typeof setInterval> | null = null;

  private readonly allMaintenanceStatusOptions = [
    { value: 1, label: 'Scheduled' },
    { value: 2, label: 'In Progress' }
  ];

  private readonly inProgressMaintenanceStatusOptions = [
    { value: 2, label: 'In Progress' }
  ];

  maintenanceStatusOptions = this.allMaintenanceStatusOptions;

  private initialStatus: number | null = null;

  get isViewLoading(): boolean {
    return this.isTimeZoneLoading || !this.updateForm;
  }

  get timezoneNoteLabel(): string | null {
    if (this.isValidZone(this.profileTimeZone)) {
      const abbreviation = getTimeZoneAbbreviation(this.profileTimeZone);
      return abbreviation ? `${this.profileTimeZone} (${abbreviation})` : this.profileTimeZone;
    }
    return this.accountTzName || this.accountTZ;
  }

  constructor(
    @Optional() public readonly dialogRef: MatDialogRef<StatusPageManageMaintenanceDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public readonly data: Maintenance | null,
    private readonly fb: FormBuilder,
    private readonly statusPageService: StatusPageService,
    private readonly authService: AuthService,
    private readonly authHttpService: AuthHTTPService,
    private readonly userStorageService: UserstorageService,
    private readonly cd: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly layoutUtilsService: LayoutUtilsService,
    private readonly profileTimeZoneService: StatusPageProfileTimeZoneService
  ) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(advancedFormat);
  }

  get timezoneAbbrev(): string {
    return getTimeZoneAbbreviation(this.getEffectivePickerTimeZone());
  }

  get isInProgressMaintenance(): boolean {
    return this.getStatusClass(this.maintenanceData?.status) === 'in-progress';
  }

  get isHistoricalMaintenance(): boolean {
    const statusClass = this.getStatusClass(this.maintenanceData?.status);
    return statusClass === 'completed' || statusClass === 'cancelled';
  }

  get isScheduledMaintenance(): boolean {
    const status = this.maintenanceData?.status;
    if (status === null || status === undefined || String(status).trim().length === 0) {
      return false;
    }
    return normalizeMaintenanceStatusClass(status, 'cancelled') === 'scheduled';
  }

  get canCancelMaintenance(): boolean {
    return this.allowDelete && !!this.maintenanceData?.id && this.isScheduledMaintenance;
  }

  ngOnInit(): void {
    this.isTimeZoneLoading = true;
    this.initializeLocaleAndDateBounds();
    this.loadAccountTimeZone();
    this.loadProfileTimeZone();
  }

  ngOnDestroy(): void {
    this.stopDateValidationRecheckLoop();
  }

  private onTimeZoneLoaded(): void {
    if (!this.accountTimeZoneResolved || !this.profileTimeZoneResolved || this.timeZoneSourcesInitialized) {
      return;
    }
    this.timeZoneSourcesInitialized = true;
    this.setTimeZoneLoading(false);
    const effectiveData = this.maintenanceData;
    if (effectiveData) {
      this.initForms(effectiveData);
    }
    this.refreshDateValidation();
    this.startDateValidationRecheckLoop();
  }

  private toPickerDate(date: Date | string | undefined): Date | null {
    if (!date) return null;
    const pickerTime = dayjs.utc(date).tz(this.getEffectivePickerTimeZone() || dayjs.tz.guess());
    return new Date(
      pickerTime.year(),
      pickerTime.month(),
      pickerTime.date(),
      pickerTime.hour(),
      pickerTime.minute(),
      0
    );
  }

  private readonly dateRangeValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
    const startValue = control.get('startDate')?.value;
    const endValue = control.get('endDate')?.value;
    if (!startValue || !endValue) return null;
    const start = this.toPickerDateTime(startValue);
    const end = this.toPickerDateTime(endValue);
    if (!start || !end) return null;
    return end.isAfter(start) ? null : { dateRangeInvalid: true };
  };

  getStartDateError(): string {
    const control = this.updateForm?.get('startDate');
    if (!control?.errors) return '';
    if (control.hasError('required')) return 'Start date is required';
    if (control.hasError('futureDate')) return 'Scheduled start date must be in the future';
    return '';
  }

  getEndDateError(): string {
    const control = this.updateForm?.get('endDate');
    if (!control) return '';
    if (control.hasError('required')) return 'End date is required';
    if (control.hasError('futureDate')) return 'Scheduled end date must be in the future';
    if (this.updateForm?.hasError('dateRangeInvalid') && (control.touched || control.dirty)) {
      return 'End date must be after start date';
    }
    return '';
  }

  private futureDateValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control?.value || control.disabled) return null;
      if (control === this.updateForm?.get('startDate') && this.isSelectedStatusInProgress()) return null;
      const date = this.toPickerDateTime(control.value);
      if (!date) return null;
      const now = this.getPickerNow();
      return date.isAfter(now) ? null : { futureDate: true };
    };
  }

  private initForms(effectiveData: Maintenance) {
    this.initialStatus = this.mapStatusValue(effectiveData.status, this.allMaintenanceStatusOptions, 1);
    this.maintenanceStatusOptions = this.initialStatus === 2
      ? this.inProgressMaintenanceStatusOptions
      : this.allMaintenanceStatusOptions;

    // Show dates for Scheduled (1) or In Progress (2)
    this.showDateEditing = [1, 2].includes(this.initialStatus);

    this.updateForm = this.fb.group({
      status: [this.initialStatus, Validators.required],
      description: [effectiveData.description || '', [Validators.required, nonWhitespaceHtmlValidator(), sanitizeInputForCrossSiteScriptCharactersDescription, this.maxTextLengthValidator(5000)]],
      startDate: [this.toPickerDate(effectiveData.startDate) || new Date(), [Validators.required, this.futureDateValidator()]],
      endDate: [this.toPickerDate(effectiveData.endDate) || new Date(), [Validators.required, this.futureDateValidator()]]
    }, { validators: this.dateRangeValidator });

    // Static editability based on initial status
    this.updateControlStates(this.initialStatus);

    this.updateForm.get('status')?.valueChanges.subscribe(val => {
      // Visibility can still be dynamic if needed, but the user requested reordering.
      // Keeping visibility logic for robust UI state, but editability is now STATIC.
      this.showDateEditing = [1, 2].includes(val);
      this.refreshDateValidation();
    });

    this.updateForm.get('description')?.valueChanges.subscribe(val => {
      const length = this.getPlainTextLength(val);
      this.descriptionLength = length === 0 ? null : length;
      this.cd.detectChanges();
    });
    // Initial length
    const initialLength = this.getPlainTextLength(effectiveData.description);
    this.descriptionLength = initialLength === 0 ? null : initialLength;
  }

  private updateControlStates(status: number) {
    if (status === 2) { // In Progress
      this.updateForm.get('startDate')?.disable();
    } else {
      this.updateForm.get('startDate')?.enable();
    }
  }

  private isSelectedStatusInProgress(): boolean {
    const selectedStatus = this.updateForm?.get('status')?.value ?? this.initialStatus;
    return Number(selectedStatus) === 2;
  }

  private initializeLocaleAndDateBounds(): void {
    try {
      const stored = this.userStorageService.getuserstorage();
      const resolved = stored ? JSON.parse(stored)?.locale : null;
      this.locale = typeof resolved === 'string' && resolved.trim().length > 0 ? resolved : 'en-US';
    } catch {
      this.locale = 'en-US';
    }
  }

  private loadAccountTimeZone(): void {
    const token = this.authService.getToken();
    if (!token) {
      this.markAccountTimeZoneResolved();
      return;
    }
    this.setTimeZoneLoading(true);
    this.statusPageService.getAccountSettings(token).subscribe({
      next: (res: any) => {
        const standardName = res?.summary?.timezone_standard_name ?? null;
        const zoneName = res?.summary?.zone_name ?? null;
        this.accountTzName = standardName || zoneName;
        if (standardName || zoneName) {
          this.getTimeZone(standardName, zoneName);
        } else {
          this.markAccountTimeZoneResolved();
        }
      },
      error: () => {
        this.markAccountTimeZoneResolved();
      }
    });
  }

  private getTimeZone(standardName?: string | null, zoneName?: string | null): void {
    if (this.isValidZone(zoneName)) {
      this.accountTZ = zoneName;
      this.markAccountTimeZoneResolved();
      return;
    }

    const lookupNames = [zoneName, standardName].filter(
      (name): name is string => !!name
    );

    this.authHttpService.getTimeZoneList().subscribe({
      next: (res: any) => {
        const mappedZone = Array.isArray(res)
          ? lookupNames
              .map((name) => res.find((item: any) => item?.name === name)?.zone)
              .find((zone) => this.isValidZone(zone))
          : null;

        this.accountTZ = mappedZone ?? null;
        this.markAccountTimeZoneResolved();
      },
      error: (error) => {
        const errorMsg = getStatusPageErrorMessage(error, 'Unable to load time zone data');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.markAccountTimeZoneResolved();
      }
    });
  }

  private markAccountTimeZoneResolved(): void {
    this.accountTimeZoneResolved = true;
    this.onTimeZoneLoaded();
  }

  private markProfileTimeZoneResolved(): void {
    this.profileTimeZoneResolved = true;
    this.onTimeZoneLoaded();
  }

  private setTimeZoneLoading(value: boolean): void {
    this.isTimeZoneLoading = value;
    this.cd.detectChanges();
  }

  private refreshDateValidation(): void {
    if (!this.updateForm) return;
    const now = this.getPickerNow().startOf('minute');
    this.minDate = {
      year: now.year(),
      month: now.month() + 1,
      day: now.date()
    };
    this.minDateTime = new Date(
      now.year(),
      now.month(),
      now.date(),
      now.hour(),
      now.minute(),
      0
    );
    this.updateForm.get('startDate')?.updateValueAndValidity();
    this.updateForm.get('endDate')?.updateValueAndValidity();
    this.updateForm.updateValueAndValidity();
    this.autoSurfaceDateErrors();
    this.cd.detectChanges();
  }

  private startDateValidationRecheckLoop(): void {
    this.stopDateValidationRecheckLoop();
    this.revalidationIntervalId = setInterval(() => {
      this.refreshDateValidation();
    }, 30000);
  }

  private stopDateValidationRecheckLoop(): void {
    if (!this.revalidationIntervalId) {
      return;
    }
    clearInterval(this.revalidationIntervalId);
    this.revalidationIntervalId = null;
  }

  private autoSurfaceDateErrors(): void {
    if (!this.updateForm) {
      return;
    }
    const startControl = this.updateForm.get('startDate');
    const endControl = this.updateForm.get('endDate');
    if (!startControl || !endControl) {
      return;
    }
    if (startControl.invalid) {
      startControl.markAsTouched();
    }
    if (endControl.invalid || this.updateForm.hasError('dateRangeInvalid')) {
      endControl.markAsTouched();
    }
  }

  private getEffectivePickerTimeZone(): string | null {
    return this.isValidZone(this.profileTimeZone) ? this.profileTimeZone : this.accountTZ;
  }

  private getPickerNow(): dayjs.Dayjs {
    const zone = this.getEffectivePickerTimeZone();
    if (this.isValidZone(zone)) {
      return dayjs().tz(zone);
    }
    return dayjs().locale(this.locale || 'en-US');
  }

  private isValidZone(value?: string | null): value is string {
    if (!value) return false;
    try {
      const tzFn = (dayjs as any).tz;
      if (typeof tzFn !== 'function') return false;
      const probe = tzFn('2020-01-01T00:00', value);
      return !!probe && typeof probe.isValid === 'function' && probe.isValid();
    } catch {
      return false;
    }
  }

  private toPickerDateTime(value: any): dayjs.Dayjs | null {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const dateString = dayjs(parsed).format('YYYY-MM-DDTHH:mm');
    const zone = this.getEffectivePickerTimeZone();
    if (this.isValidZone(zone)) {
      return dayjs.tz(dateString, zone);
    }
    return dayjs(parsed).locale(this.locale || 'en-US');
  }

  private mapStatusValue(
    status: unknown,
    options: { value: number; label: string }[],
    fallback: number
  ): number {
    if (typeof status === 'number' && Number.isFinite(status)) {
      return status;
    }
    if (typeof status === 'string') {
      const parsedStatus = Number(status.trim());
      if (Number.isFinite(parsedStatus) && options.some(option => option.value === parsedStatus)) {
        return parsedStatus;
      }

      const normalized = status.toLowerCase().replaceAll(/[\s_-]/g, '');
      const match = options.find(option => option.label.toLowerCase().replaceAll(/[\s_-]/g, '') === normalized);
      if (match) {
        return match.value;
      }
    }
    return fallback;
  }

  onCancel() {
    this.close();
  }

  onToggleExpand() {
    this.toggleExpand.emit();
  }


  getStatusClass(status: any): string {
    return normalizeMaintenanceStatusClass(status, 'scheduled');
  }

  getMaintenanceStatusLabel(status: any): string {
    return getMaintenanceStatusDisplayLabel(status, 'scheduled');
  }


  onCancelMaintenance() {
    const title = 'Cancel Maintenance?';
    const description = `Are you sure you want to cancel this maintenance "${this.maintenanceData?.title}"?`;
    const waitDesciption = 'Maintenance is being cancelled...';
    const cancelMessage = `Maintenance cancelled successfully.`;

    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDesciption, 'Confirm');

    dialogRef.afterClosed().subscribe(confirm => {
      if (confirm) {
        const token = this.authService.getToken();
        const id = this.maintenanceData?.id;
        if (!id) return;

        this.statusPageService.cancelMaintenanceV3(token, id).subscribe({
          next: () => {
            this.layoutUtilsService.showActionNotification(cancelMessage, MessageType.Delete, environment.TOAST_MESSAGE_TIME, true, false);
            this.close({ action: 'cancelled' });
          },
          error: (err) => {
            const errorMsg = getStatusPageErrorMessage(err, 'Error cancelling maintenance');
            this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
          }
        });
      }
    });
  }

  onSaveMainStatus() {
    if (this.isSaving || this.isViewLoading || this.isHistoricalMaintenance) return;
    this.applyCurrentStartTimeForEarlyInProgress();
    this.updateForm.markAllAsTouched();
    if (this.updateForm.invalid) return;

    const token = this.authService.getToken();
    const id = this.maintenanceData?.id;
    if (!id) return;

    const maintenance = this.maintenanceData;
    const formValue = this.updateForm.getRawValue();
    const payload: UpdateMaintenancePayload = {
      title: maintenance?.title || '',
      description: formValue.description,
      scheduledStartDate: this.formatDateToISO(formValue.startDate || maintenance?.startDate),
      scheduledEndDate: this.formatDateToISO(formValue.endDate || maintenance?.endDate),
      status: Number(formValue.status),
      notifySubscribers: maintenance?.notifySubscribers ?? true,
      reminderHoursBeforeStart: this.getFiniteNumberOrDefault(maintenance?.reminderHoursBeforeStart, 0),
      sendReminderAtStart: maintenance?.sendReminderAtStart ?? true,
      sendReminderAtEnd: maintenance?.sendReminderAtEnd ?? true,
      autoStartEnabled: maintenance?.autoStartEnabled ?? true,
      autoCompleteEnabled: maintenance?.autoCompleteEnabled ?? true
    };

    this.isSaving = true;
    this.statusPageService.updateMaintenanceV3(token, id, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.layoutUtilsService.showActionNotification('Maintenance updated successfully', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
        this.refresh.emit();
        this.close({ action: 'updated' });
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error updating maintenance status');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isSaving = false;
        this.cd.detectChanges();
      }
    });
  }

  private applyCurrentStartTimeForEarlyInProgress(): void {
    if (!this.updateForm || this.initialStatus !== 1 || !this.isSelectedStatusInProgress()) {
      return;
    }

    const startControl = this.updateForm.get('startDate');
    if (!startControl) {
      return;
    }

    const scheduledStart = this.toPickerDateTime(startControl.value || this.maintenanceData?.startDate);
    const pickerNow = this.getPickerNow().startOf('minute');
    if (scheduledStart && !scheduledStart.isAfter(pickerNow)) {
      return;
    }

    startControl.setValue(this.toPickerDate(pickerNow.toISOString()));
    startControl.markAsDirty();
    startControl.updateValueAndValidity({ emitEvent: false });
    this.updateForm.updateValueAndValidity({ emitEvent: false });
    this.cd.detectChanges();
  }

  onMarkAsCompleted() {
    if (this.isSaving || this.isViewLoading || this.isHistoricalMaintenance) return;
    const token = this.authService.getToken();
    const maintenanceId = this.maintenanceData?.id;
    if (!maintenanceId) return;

    const payload: CompleteMaintenancePayload = {
      actualEndDate: new Date().toISOString()
    };
    this.isSaving = true;
    this.statusPageService.completeMaintenanceV3(token, maintenanceId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.layoutUtilsService.showActionNotification('Maintenance marked as completed', MessageType.Update, environment.TOAST_MESSAGE_TIME, true, false);
        this.refresh.emit();
        this.close({ action: 'completed' });
      },
      error: (err) => {
        const errorMsg = getStatusPageErrorMessage(err, 'Error completing maintenance');
        this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
        this.isSaving = false;
        this.cd.detectChanges();
      }
    });
  }

  private getFiniteNumberOrDefault(value: unknown, fallback: number): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }

  private getPlainTextLength(value: unknown): number {
    return stripHtmlToText(value).length;
  }

  sanitizeHtmlContent(value: unknown): string {
    return normalizeStatusPageRichText(value);
  }

  private maxTextLengthValidator(maxLength: number) {
    return (control: AbstractControl) => {
      const length = this.getPlainTextLength(control?.value);
      return length > maxLength ? { maxTextLength: { requiredLength: maxLength, actualLength: length } } : null;
    };
  }

  onContentChanged(event: any) {
    // Already handled by valueChanges subscription, but kept for consistency if needed by template
    const length = this.getPlainTextLength(event.html);
    this.descriptionLength = length === 0 ? null : length;
    this.cd.detectChanges();
  }

  private close(result?: any) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }
    this.closed.emit(result);
  }

  /**
   * Converts a picker Date to a UTC ISO string, always treating the
   * picker value as profile-local time, with account timezone fallback
   * (not browser local time).
   */
  private formatDateToISO(value: any): string {
    if (!value) return new Date().toISOString();
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return new Date().toISOString();

    // Extract wall-clock components from the picker Date (browser-local).
    // These represent the user's intended picker-timezone local time.
    const dateString = dayjs(parsed).format('YYYY-MM-DDTHH:mm:ss');
    const zone = this.getEffectivePickerTimeZone();

    if (this.isValidZone(zone)) {
      const asPickerLocal = dayjs.tz(dateString, zone);
      if (asPickerLocal.isValid()) {
        return asPickerLocal.toISOString();
      }
    }

    // Fallback: no timezone info — send as-is treating as UTC
    console.warn('formatDateToISO: picker timezone not set, sending raw date string as UTC (may be inaccurate)');
    return dayjs.utc(dateString).toISOString();
  }

  formatDateInPickerTz(date: Date | string | undefined, format: string = 'MMM d, y, h:mm a'): string {
    return formatDateInTimeZone(date, this.getEffectivePickerTimeZone(), format);
  }

  private loadProfileTimeZone(): void {
    this.profileTimeZoneService.getProfileTimeZone().pipe(take(1)).subscribe({
      next: (timeZone) => {
        this.profileTimeZone = timeZone;
        this.markProfileTimeZoneResolved();
      },
      error: () => {
        this.profileTimeZone = null;
        this.markProfileTimeZoneResolved();
      }
    });
  }
}





